import { createContext, useCallback, useContext, useMemo, useState } from "react"
import PricingModal from "../components/PricingModal"

// Every upgrade button in the signed-in app opens the pricing box, never
// Stripe directly (#336).
//
// The buttons used to call startCheckout() with its default monthly period:
// Dashboard's `handleUpgrade`, this hook, Profile and the Developers page each
// had a copy. So the owner went from "Editing is locked" straight to a monthly
// card form, without the price, the annual option, or the line that the code
// keeps working whether they pay or not. PricingModal has all three, so the app
// mounts one (UpgradeProvider, in App) and the buttons open it. The checkout
// call lives in the modal.
//
//   const { upgrade } = useUpgrade()
//   upgrade()                                  // the default copy
//   upgrade("shapes")                          // a PricingModal reason
//   upgrade("locked", { destination: url })    // the code lock wall
//
// `upgrade` is safe as a bare onClick: a click event is not a reason, so it
// reads as the default.
const UpgradeContext = createContext(null)

export function UpgradeProvider({ children }) {
  const [request, setRequest] = useState(null) // { reason, destination } while open

  const upgrade = useCallback((reason, details) => {
    setRequest({
      reason: typeof reason === "string" ? reason : null,
      destination: details?.destination || null
    })
  }, [])
  const close = useCallback(() => setRequest(null), [])
  const value = useMemo(() => ({ upgrade }), [upgrade])

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <PricingModal
        open={!!request}
        onClose={close}
        reason={request?.reason}
        destination={request?.destination}
        signedIn
      />
    </UpgradeContext.Provider>
  )
}

// Outside the provider (nothing mounts one there today) a button still goes
// somewhere real: the pricing page.
const FALLBACK = { upgrade: () => window.location.assign("/pricing") }

export default function useUpgrade() {
  return useContext(UpgradeContext) || FALLBACK
}
